import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Phone, CalendarCheck, ShieldCheck, ArrowRight, MapPin, Globe, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { api, formatApiError } from "../lib/api";
import { withDemoFlag } from "../lib/links";

export default function AgentPublicPage() {
    const { slug } = useParams();
    const [agent, setAgent] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await api.get(`/public/agents/${slug}`);
                if (mounted) setAgent(data);
            } catch (err) {
                if (mounted) setError(formatApiError(err));
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-slate-700" data-testid="agent-public-loading">Loading…</div>
        );
    }
    if (error || !agent) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="agent-public-error">
                <div className="text-center max-w-md">
                    <h1 className="font-serif text-3xl text-navy-900">Agent not found</h1>
                    <p className="mt-3 text-slate-600">This agent profile is unavailable. Please check the link or visit our homepage.</p>
                    <Link to="/" className="inline-block mt-6"><Button className="bg-navy-900 text-white rounded-md">Go to homepage</Button></Link>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white" data-testid="agent-public-page">
            <section className="relative overflow-hidden bg-navy-900 text-white py-20 md:py-28">
                {/* Brand gradient + light blooms (matches site-wide hero) */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-[#0B2F3A] to-emerald-900/70" />
                    <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-amber-400/15 blur-3xl" />
                    <div className="absolute -bottom-32 -left-20 w-[420px] h-[420px] rounded-full bg-emerald-400/15 blur-3xl" />
                </div>
                <div className="relative max-w-5xl mx-auto px-6 sm:px-8 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-4 flex justify-center md:justify-start">
                        {agent.photo_url ? (
                            <div className="relative">
                                <div className="absolute -inset-2 rounded-2xl bg-gradient-to-br from-amber-400 via-rose-400 to-emerald-400 blur opacity-60" />
                                <img src={agent.photo_url} alt={agent.name} className="relative w-52 md:w-60 aspect-[3/4] rounded-2xl object-cover object-top border-4 border-white shadow-2xl" data-testid="agent-photo" />
                            </div>
                        ) : (
                            <div className="w-52 md:w-60 aspect-[3/4] rounded-2xl bg-gradient-to-br from-amber-400 to-emerald-400 flex items-center justify-center text-navy-900 font-serif text-7xl border-4 border-white shadow-2xl">
                                {(agent.name || "?").slice(0, 1)}
                            </div>
                        )}
                    </div>
                    <div className="md:col-span-8">
                        <div className="inline-flex items-center gap-2 uppercase tracking-[0.25em] text-[11px] font-bold text-amber-300 mb-4 bg-amber-400/10 border border-amber-400/30 rounded-full px-4 py-1.5">
                            <Sparkles className="w-3 h-3" /> Licensed Insurance Professional
                        </div>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight" data-testid="agent-name">{agent.name}</h1>
                        {agent.bio && <p className="mt-6 text-lg text-blue-100/90 max-w-2xl leading-relaxed" data-testid="agent-bio">{agent.bio}</p>}
                        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-sm text-blue-100/85">
                            {agent.phone && (
                                <a href={`tel:${agent.phone}`} className="inline-flex items-center gap-2 hover:text-amber-300 transition-colors" data-testid="agent-phone">
                                    <span className="w-7 h-7 rounded-md bg-emerald-400/20 text-emerald-300 flex items-center justify-center"><Phone className="w-3.5 h-3.5" /></span> {agent.phone}
                                </a>
                            )}
                            {agent.license_states?.length > 0 && (
                                <div className="inline-flex items-center gap-2" data-testid="agent-states">
                                    <span className="w-7 h-7 rounded-md bg-amber-400/20 text-amber-300 flex items-center justify-center"><MapPin className="w-3.5 h-3.5" /></span> Licensed in {agent.license_states.join(", ")}
                                </div>
                            )}
                            {agent.website_url && (
                                <a href={agent.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-amber-300 transition-colors" data-testid="agent-website">
                                    <span className="w-7 h-7 rounded-md bg-sky-400/20 text-sky-300 flex items-center justify-center"><Globe className="w-3.5 h-3.5" /></span> {agent.website_url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                                </a>
                            )}
                        </div>
                        <div className="mt-10 flex flex-col sm:flex-row gap-3">
                            <Link to={withDemoFlag(`/review?agent=${encodeURIComponent(agent.slug)}`)} data-testid="agent-cta-review">
                                <Button className="bg-amber-400 text-slate-900 hover:bg-amber-300 hover:-translate-y-0.5 transition-all px-8 py-7 rounded-md font-bold w-full sm:w-auto shadow-lg shadow-amber-500/25">
                                    Request a Free Annuity Review <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </Link>
                            {agent.calendly_link && (
                                <a href={agent.calendly_link} target="_blank" rel="noreferrer" data-testid="agent-cta-calendly">
                                    <Button variant="outline" className="bg-white/5 backdrop-blur border-white/40 text-white hover:bg-white/15 hover:text-white px-8 py-7 rounded-md w-full sm:w-auto">
                                        <CalendarCheck className="w-4 h-4 mr-2" /> Book a Call
                                    </Button>
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-gradient-to-b from-slate-50 to-white">
                <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
                    <div className="inline-flex w-14 h-14 rounded-xl bg-emerald-100 border border-emerald-200 items-center justify-center text-emerald-700 mb-4">
                        <ShieldCheck className="w-7 h-7" />
                    </div>
                    <h2 className="font-serif text-3xl sm:text-4xl text-navy-900">Clarity, not pressure.</h2>
                    <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                        {agent.name.split(" ")[0]} will review your existing annuity contract — fees, surrender schedule, income rider, and real guarantees vs assumptions — so you can make an informed decision about what you own.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-slate-700">
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> No fees</span>
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" /> No follow-up sales calls</span>
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-sky-500" /> Written summary you keep</span>
                    </div>
                    {agent.calendly_link && (
                        <div className="mt-12 bg-white border border-slate-200 rounded-2xl p-4 shadow-xl relative overflow-hidden">
                            <span className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-rose-400 to-emerald-400" />
                            <iframe
                                data-testid="agent-calendly-embed"
                                title={`Schedule with ${agent.name}`}
                                src={agent.calendly_link}
                                style={{ width: "100%", minHeight: "720px", border: 0 }}
                            />
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
